import { useCallback, useMemo, useRef, useState } from 'react';

import type { DragEvent } from 'react';

interface UseDropTargetOptions {
  /** DataTransfer MIME type this target accepts. */
  type: string;
  onDrop: (data: string) => void;
  enabled?: boolean;
}

/**
 * HTML5 drop target with a depth-counted "is the drag over me" flag.
 *
 * Chrome fires `dragleave` with a null `relatedTarget` whenever the pointer
 * crosses into a child element (crbug 131325), so `contains(relatedTarget)`
 * guards flip the state on every bubble boundary — and every flip re-renders
 * the whole target subtree. Counting `dragenter`/`dragleave` pairs instead
 * means `isDragOver` changes exactly once on entry and once on exit, and
 * `dragover` (which fires continuously) never touches state at all.
 */
export const useDropTarget = ({ type, onDrop, enabled = true }: UseDropTargetOptions) => {
  const depthRef = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);

  const accepts = useCallback(
    (e: DragEvent) => enabled && Array.from(e.dataTransfer.types).includes(type),
    [enabled, type],
  );

  const reset = useCallback(() => {
    depthRef.current = 0;
    setIsDragOver(false);
  }, []);

  const onDragEnter = useCallback(
    (e: DragEvent) => {
      if (!accepts(e)) return;
      e.preventDefault();
      depthRef.current += 1;
      if (depthRef.current === 1) setIsDragOver(true);
    },
    [accepts],
  );

  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (!accepts(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    },
    [accepts],
  );

  const onDragLeave = useCallback(
    (e: DragEvent) => {
      if (!accepts(e) || depthRef.current === 0) return;
      depthRef.current -= 1;
      if (depthRef.current === 0) setIsDragOver(false);
    },
    [accepts],
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      if (!accepts(e)) return;
      e.preventDefault();
      reset();
      const data = e.dataTransfer.getData(type);
      if (data) onDrop(data);
    },
    [accepts, onDrop, reset, type],
  );

  const dragHandlers = useMemo(
    () => ({ onDragEnter, onDragOver, onDragLeave, onDrop: handleDrop }),
    [onDragEnter, onDragOver, onDragLeave, handleDrop],
  );

  return { isDragOver, dragHandlers };
};
